import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { StatusPill } from "@/components/StatusPill";
import { apiClient, auth, formatMoney, STATUS_LABELS, STATUS_ORDER } from "@/lib/api";
import { TIDS } from "@/lib/testIds";
import { toast } from "sonner";
import { Users, Briefcase, BadgeCheck, DollarSign, Search, ArrowRight, KeyRound, Copy, Trash2, Plus, UserPlus, Activity, History, Pencil } from "lucide-react";
import { EditEmployeeDialog } from "@/components/EditEmployeeDialog";
import { EditPartnerDialog } from "@/components/EditPartnerDialog";

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <Card className="rounded-xl border-slate-200 shadow-sm p-6">
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="mt-2 text-3xl font-heading font-medium text-slate-900 tracking-tight">{value}</div>
      </div>
      <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}>
        <Icon className="h-5 w-5" />
      </span>
    </div>
  </Card>
);

const ACTION_CONF = {
  "referral.deleted":      { color: "bg-rose-50 text-rose-700 border-rose-200",        verb: "deleted a referral" },
  "referral.updated":      { color: "bg-blue-50 text-blue-700 border-blue-200",        verb: "updated a referral" },
  "employee.created":      { color: "bg-emerald-50 text-emerald-700 border-emerald-200", verb: "created an employee" },
  "employee.deleted":      { color: "bg-rose-50 text-rose-700 border-rose-200",        verb: "deleted an employee" },
  "invite_code.created":   { color: "bg-blue-50 text-blue-700 border-blue-200",        verb: "created an invite code" },
  "invite_code.revoked":   { color: "bg-amber-50 text-amber-700 border-amber-200",     verb: "revoked an invite code" },
};

const formatChange = (k, v) => {
  if (!v) return null;
  const f = v.from ?? "—";
  const t = v.to ?? "—";
  if (k === "commission_paid") return `commission_paid: ${f ? "yes" : "no"} → ${t ? "yes" : "no"}`;
  return `${k}: ${f} → ${t}`;
};

const AuditRow = ({ a }) => {
  const conf = ACTION_CONF[a.action] || { color: "bg-slate-100 text-slate-700 border-slate-200", verb: a.action };
  const when = new Date(a.created_at);
  const changes = a.metadata?.changes;
  return (
    <li className="p-4 flex items-start gap-3">
      <span className={`inline-flex items-center text-[11px] font-medium uppercase tracking-wider px-2 py-1 rounded-full border ${conf.color} whitespace-nowrap`}>
        {a.action.split(".")[1] || a.action}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-slate-900">
          <span className="font-medium">{a.actor_name || a.actor_email || a.actor_role || "Someone"}</span>{" "}
          <span className="text-slate-500">{conf.verb}</span>
          {a.target_label ? (
            <>
              {" "}
              <span className="font-medium text-slate-900">{a.target_label}</span>
            </>
          ) : null}
        </div>
        {changes ? (
          <ul className="mt-1 text-xs text-slate-500 space-y-0.5">
            {Object.entries(changes).map(([k, v]) => (
              <li key={k}><code className="font-mono">{formatChange(k, v)}</code></li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className="text-xs text-slate-400 whitespace-nowrap" title={when.toString()}>
        {when.toLocaleDateString()} · {when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      </div>
    </li>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [inviteCodes, setInviteCodes] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ label: "", expires_days: "" });
  const [createOpen, setCreateOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, p, r, e, ic, al] = await Promise.all([
        apiClient.get("/admin/stats"),
        apiClient.get("/admin/partners"),
        apiClient.get("/referrals"),
        apiClient.get("/admin/employees"),
        apiClient.get("/admin/invite-codes"),
        apiClient.get("/admin/audit-logs?limit=100"),
      ]);
      setStats(s.data);
      setPartners(p.data);
      setReferrals(r.data);
      setEmployees(e.data);
      setInviteCodes(ic.data);
      setAuditLogs(al.data);
    } catch (err) {
      toast.error("Could not load admin data");
      if (err?.response?.status === 401) navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (auth.getRole() !== "admin") {
      navigate("/admin/login");
      return;
    }
    loadAll();
  }, [navigate, loadAll]);

  const createInvite = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { label: createForm.label || null };
      if (createForm.expires_days) payload.expires_days = Number(createForm.expires_days);
      await apiClient.post("/admin/invite-codes", payload);
      toast.success("Invite code created");
      setCreateForm({ label: "", expires_days: "" });
      setCreateOpen(false);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create invite");
    } finally {
      setCreating(false);
    }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this invite code?")) return;
    try {
      await apiClient.delete(`/admin/invite-codes/${id}`);
      toast.success("Code revoked");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not revoke");
    }
  };

  const copyInviteLink = async (code) => {
    const url = `${window.location.origin}/employee/signup?code=${code}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Invite link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const deleteReferral = async (r) => {
    if (!window.confirm(`Delete referral for "${r.business_name}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/referrals/${r.id}`);
      toast.success("Referral deleted");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const deleteEmployee = async (e) => {
    if (!window.confirm(`Delete employee ${e.full_name}? Partners they recruited will be unassigned.`)) return;
    try {
      await apiClient.delete(`/admin/employees/${e.id}`);
      toast.success("Employee deleted");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const [addEmpOpen, setAddEmpOpen] = useState(false);
  const [addEmpForm, setAddEmpForm] = useState({ full_name: "", email: "", mobile: "", password: "" });
  const [addingEmp, setAddingEmp] = useState(false);
  const addEmployee = async (ev) => {
    ev.preventDefault();
    if (addEmpForm.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setAddingEmp(true);
    try {
      const res = await apiClient.post("/admin/employees", addEmpForm);
      if (addEmpForm.mobile) {
        if (res.data?.sms_sent) {
          toast.success("Employee created — login SMS sent");
        } else {
          toast.warning("Employee created — SMS could not be sent (share credentials manually)");
        }
      } else {
        toast.success("Employee created");
      }
      setAddEmpForm({ full_name: "", email: "", mobile: "", password: "" });
      setAddEmpOpen(false);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create employee");
    } finally {
      setAddingEmp(false);
    }
  };

  const deletePartner = async (p) => {
    const count = p.referral_count || 0;
    const msg = count > 0
      ? `Delete partner ${p.full_name}? This will also remove ${count} referral${count === 1 ? "" : "s"} they submitted. This cannot be undone.`
      : `Delete partner ${p.full_name}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await apiClient.delete(`/admin/partners/${p.id}`);
      toast.success(`Partner deleted${count ? ` (${count} referral${count === 1 ? "" : "s"} removed)` : ""}`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = useMemo(() => {
    return referrals.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (partnerFilter !== "all" && r.partner_id !== partnerFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !r.business_name?.toLowerCase().includes(q) &&
          !r.business_owner_name?.toLowerCase().includes(q) &&
          !r.owner_email?.toLowerCase().includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [referrals, statusFilter, partnerFilter, search]);

  return (
    <DashboardShell
      role="admin"
      title="Operations dashboard"
      subtitle="Drill down through your team: employees → referral partners → clients."
    >
      <div data-testid={TIDS.adminDashboard} className="space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <StatCard icon={Users} label="Partners" value={stats?.total_partners ?? 0} accent="bg-blue-50 text-blue-700" />
          <StatCard icon={Briefcase} label="Referrals" value={stats?.total_referrals ?? 0} accent="bg-indigo-50 text-indigo-700" />
          <StatCard icon={BadgeCheck} label="Funded deals" value={stats?.funded_count ?? 0} accent="bg-emerald-50 text-emerald-700" />
          <StatCard icon={DollarSign} label="Funded volume" value={formatMoney(stats?.total_funded_amount ?? 0)} accent="bg-amber-50 text-amber-700" />
        </div>

        <Tabs defaultValue="employees">
          <TabsList className="bg-white border border-slate-200 rounded-full p-1">
            <TabsTrigger value="employees" data-testid={TIDS.adminTabEmployees} className="rounded-full px-5">Employees</TabsTrigger>
            <TabsTrigger value="partners" data-testid={TIDS.adminTabPartners} className="rounded-full px-5">Partners</TabsTrigger>
            <TabsTrigger value="referrals" data-testid={TIDS.adminTabReferrals} className="rounded-full px-5">All clients</TabsTrigger>
            <TabsTrigger value="activity" data-testid={TIDS.adminTabActivity} className="rounded-full px-5">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="referrals" className="mt-5">
            <Card className="rounded-xl border-slate-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-slate-100 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex flex-wrap gap-3 items-center">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search business / owner / email"
                      data-testid={TIDS.adminFilterSearch}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger data-testid={TIDS.adminFilterStatus} className="w-48"><SelectValue placeholder="All statuses" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                    <SelectTrigger data-testid={TIDS.adminFilterPartner} className="w-56"><SelectValue placeholder="All partners" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All partners</SelectItem>
                      {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-sm text-slate-500">{filtered.length} result{filtered.length === 1 ? "" : "s"}</span>
              </div>
              {loading ? (
                <div className="p-10 text-center text-slate-500">Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="p-10 text-center text-slate-500">No referrals match the current filters.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Business</TableHead>
                      <TableHead>Partner</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Funding</TableHead>
                      <TableHead className="text-right">Commission</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((r) => (
                      <TableRow key={r.id} data-testid={TIDS.referralRow}>
                        <TableCell>
                          <div className="font-medium text-slate-900">{r.business_name}</div>
                          <div className="text-xs text-slate-500">{r.business_owner_name}</div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-700">
                          {r.partner_snapshot?.full_name}
                          <div className="text-xs text-slate-500">{r.partner_snapshot?.email}</div>
                        </TableCell>
                        <TableCell><StatusPill status={r.status} /></TableCell>
                        <TableCell className="text-right text-slate-700">
                          {r.funding_amount ? formatMoney(r.funding_amount) : <span className="text-slate-400">{formatMoney(r.funding_amount_needed)}</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          {r.commission_amount ? (
                            <span className={r.commission_paid ? "text-emerald-700 font-medium" : "text-slate-900 font-medium"}>
                              {formatMoney(r.commission_amount)}
                            </span>
                          ) : <span className="text-slate-500">{formatMoney(r.estimated_commission)}</span>}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <Link to={`/admin/referrals/${r.id}`}>
                            <Button variant="ghost" size="sm" data-testid={TIDS.referralOpen} className="hover:text-blue-700">
                              Manage <ArrowRight className="ml-1 h-4 w-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={TIDS.adminReferralDelete}
                            onClick={() => deleteReferral(r)}
                            title="Delete referral"
                            className="hover:text-rose-600 ml-1"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="partners" className="mt-5">
            <Card className="rounded-xl border-slate-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-lg font-medium text-slate-900">Referral partners</h2>
                <span className="text-sm text-slate-500">{partners.length} total</span>
              </div>
              {partners.length === 0 ? (
                <div className="p-10 text-center text-slate-500">No partners signed up yet.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Referred by</TableHead>
                      <TableHead className="text-right">Recruits</TableHead>
                      <TableHead className="text-right">Referrals</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partners.map((p) => (
                      <TableRow
                        key={p.id}
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => navigate(`/admin/partners/${p.id}`)}
                      >
                        <TableCell className="font-medium text-slate-900">{p.full_name}</TableCell>
                        <TableCell className="text-slate-700">{p.email}</TableCell>
                        <TableCell className="text-slate-700">{p.mobile}</TableCell>
                        <TableCell className="text-slate-700">{p.company_name || "—"}</TableCell>
                        <TableCell className="text-slate-700">
                          {p.referred_by_name ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700">
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                              {p.referred_by_name}
                            </span>
                          ) : <span className="text-slate-400">—</span>}
                        </TableCell>
                        <TableCell className="text-right font-medium">{p.recruits_count || 0}</TableCell>
                        <TableCell className="text-right font-medium">{p.referral_count}</TableCell>
                        <TableCell className="text-slate-500 text-sm">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost" size="sm"
                            onClick={(ev) => { ev.stopPropagation(); navigate(`/admin/partners/${p.id}`); }}
                            className="hover:text-blue-700 mr-1"
                          >
                            View <ArrowRight className="ml-1 h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            data-testid={TIDS.adminPartnerEdit}
                            onClick={(ev) => { ev.stopPropagation(); setEditingPartner(p); }}
                            title="Edit partner"
                            className="hover:text-blue-700"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            onClick={(ev) => { ev.stopPropagation(); deletePartner(p); }}
                            title="Delete partner"
                            className="hover:text-rose-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="employees" className="mt-5">
            <div className="grid lg:grid-cols-5 gap-6">
              {/* Employees list */}
              <Card className="lg:col-span-3 rounded-xl border-slate-200 shadow-sm overflow-hidden">
                <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="text-lg font-medium text-slate-900">Employees</h2>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-500">{employees.length} total</span>
                    <Dialog open={addEmpOpen} onOpenChange={setAddEmpOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" data-testid={TIDS.adminAddEmployee} className="rounded-full bg-blue-600 hover:bg-blue-700">
                          <UserPlus className="h-4 w-4 mr-1" /> Add employee
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader><DialogTitle>Add employee directly</DialogTitle></DialogHeader>
                        <p className="text-sm text-slate-500 -mt-2">Creates an employee without using an invite code. If you provide their mobile, we&apos;ll text them a login link automatically.</p>
                        <form onSubmit={addEmployee} className="space-y-4 mt-2">
                          <div className="space-y-2">
                            <Label>Full name</Label>
                            <Input value={addEmpForm.full_name} onChange={(ev) => setAddEmpForm({ ...addEmpForm, full_name: ev.target.value })} required />
                          </div>
                          <div className="space-y-2">
                            <Label>Work email</Label>
                            <Input type="email" value={addEmpForm.email} onChange={(ev) => setAddEmpForm({ ...addEmpForm, email: ev.target.value })} required />
                          </div>
                          <div className="space-y-2">
                            <Label>Mobile <span className="text-xs text-slate-500">(optional — auto-SMS login link)</span></Label>
                            <Input type="tel" placeholder="+1 555 123 4567" value={addEmpForm.mobile} onChange={(ev) => setAddEmpForm({ ...addEmpForm, mobile: ev.target.value })} />
                          </div>
                          <div className="space-y-2">
                            <Label>Temporary password <span className="text-xs text-slate-500">(min 8 chars)</span></Label>
                            <Input type="text" value={addEmpForm.password} onChange={(ev) => setAddEmpForm({ ...addEmpForm, password: ev.target.value })} minLength={8} required />
                          </div>
                          <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setAddEmpOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" disabled={addingEmp} data-testid={TIDS.adminAddEmployeeSubmit} className="rounded-full bg-blue-600 hover:bg-blue-700">
                              {addingEmp ? "Creating…" : "Create employee"}
                            </Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                {employees.length === 0 ? (
                  <div className="p-10 text-center text-slate-500">
                    No employees yet. Add one directly above, or create an invite code.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="text-right">Partners</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employees.map((e) => (
                        <TableRow
                          key={e.id}
                          data-testid={TIDS.adminEmployeeRow}
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => navigate(`/admin/employees/${e.id}`)}
                        >
                          <TableCell className="font-medium text-slate-900">{e.full_name}</TableCell>
                          <TableCell className="text-slate-700">{e.email}</TableCell>
                          <TableCell className="text-right font-medium">{e.partners_recruited}</TableCell>
                          <TableCell className="text-slate-500 text-sm">{new Date(e.created_at).toLocaleDateString()}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={TIDS.adminEmployeeRowDrill}
                              onClick={(ev) => { ev.stopPropagation(); navigate(`/admin/employees/${e.id}`); }}
                              className="hover:text-blue-700 mr-1"
                            >
                              View <ArrowRight className="ml-1 h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              data-testid={TIDS.adminEmployeeEdit}
                              onClick={(ev) => { ev.stopPropagation(); setEditingEmployee(e); }}
                              title="Edit employee"
                              className="hover:text-blue-700"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              data-testid={TIDS.adminEmployeeDelete}
                              onClick={(ev) => { ev.stopPropagation(); deleteEmployee(e); }}
                              title="Delete employee"
                              className="hover:text-rose-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>

              {/* Invite codes panel */}
              <Card className="lg:col-span-2 rounded-xl border-slate-200 shadow-sm overflow-hidden">
                <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-slate-500" />
                    <h2 className="text-lg font-medium text-slate-900">Invite codes</h2>
                  </div>
                  <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" data-testid={TIDS.adminInviteCreate} className="rounded-full bg-blue-600 hover:bg-blue-700">
                        <Plus className="h-4 w-4 mr-1" /> New
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader><DialogTitle>Create invite code</DialogTitle></DialogHeader>
                      <form onSubmit={createInvite} className="space-y-4 mt-2">
                        <div className="space-y-2">
                          <Label>Label <span className="text-xs text-slate-500">(internal, optional)</span></Label>
                          <Input
                            placeholder="e.g. Q1 hires, Jane Doe"
                            value={createForm.label}
                            onChange={(ev) => setCreateForm({ ...createForm, label: ev.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Expires in (days) <span className="text-xs text-slate-500">(blank = never)</span></Label>
                          <Input
                            type="number" min="1"
                            placeholder="14"
                            value={createForm.expires_days}
                            onChange={(ev) => setCreateForm({ ...createForm, expires_days: ev.target.value })}
                          />
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="rounded-full">Cancel</Button>
                          <Button type="submit" disabled={creating} className="rounded-full bg-blue-600 hover:bg-blue-700">
                            {creating ? "Creating…" : "Create code"}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
                {inviteCodes.length === 0 ? (
                  <div className="p-10 text-center text-slate-500">
                    No invite codes yet.
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {inviteCodes.map((c) => {
                      const expired = c.expires_at && new Date(c.expires_at) < new Date();
                      return (
                        <li
                          key={c.id}
                          data-testid={TIDS.adminInviteCodeRow}
                          className="p-4 flex items-start justify-between gap-3"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <code className="font-mono text-sm font-medium text-slate-900 bg-slate-50 px-2 py-0.5 rounded">{c.code}</code>
                              {c.used ? (
                                <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">used</span>
                              ) : expired ? (
                                <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">expired</span>
                              ) : (
                                <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">active</span>
                              )}
                            </div>
                            {c.label ? <div className="text-xs text-slate-600 mt-1">{c.label}</div> : null}
                            {c.used_by_employee ? (
                              <div className="text-xs text-slate-500 mt-1">→ {c.used_by_employee.full_name} ({c.used_by_employee.email})</div>
                            ) : null}
                            <div className="text-xs text-slate-400 mt-1">
                              {new Date(c.created_at).toLocaleDateString()}{c.expires_at ? ` · expires ${new Date(c.expires_at).toLocaleDateString()}` : ""}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {!c.used && !expired ? (
                              <Button size="icon" variant="ghost" data-testid={TIDS.adminInviteCopy} onClick={() => copyInviteLink(c.code)} title="Copy invite link">
                                <Copy className="h-4 w-4" />
                              </Button>
                            ) : null}
                            {!c.used ? (
                              <Button size="icon" variant="ghost" data-testid={TIDS.adminInviteRevoke} onClick={() => revokeInvite(c.id)} title="Revoke" className="hover:text-rose-600">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="activity" className="mt-5">
            <Card className="rounded-xl border-slate-200 shadow-sm overflow-hidden">
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <History className="h-4 w-4 text-slate-500" />
                  <h2 className="text-lg font-medium text-slate-900">Activity log</h2>
                </div>
                <span className="text-sm text-slate-500">{auditLogs.length} recent events</span>
              </div>
              {auditLogs.length === 0 ? (
                <div className="p-10 text-center text-slate-500">No activity yet.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {auditLogs.map((a) => <AuditRow key={a.id} a={a} />)}
                </ul>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <EditEmployeeDialog
        employee={editingEmployee}
        open={!!editingEmployee}
        onOpenChange={(o) => { if (!o) setEditingEmployee(null); }}
        onSaved={loadAll}
      />

      <EditPartnerDialog
        partner={editingPartner}
        open={!!editingPartner}
        onOpenChange={(o) => { if (!o) setEditingPartner(null); }}
        onSaved={loadAll}
      />
    </DashboardShell>
  );
}
